import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { useSendMessage } from '../../services/api/contacts-api';

const Section = styled.section`
  padding: 2rem;
  border-radius: 28px;
  background: ${({ $palette }) => $palette.surface};
  border: 1px solid ${({ $palette }) => $palette.border};
  color: ${({ $palette }) => $palette.text};
  box-shadow: ${({ $palette }) => $palette.shadow};
`;

const Header = styled.div`
  display: grid;
  gap: 0.35rem;
  margin-bottom: 1.5rem;
`;

const Eyebrow = styled.p`
  margin: 0;
  font-size: 0.8rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $palette }) => $palette.muted};
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 40rem;
  line-height: 1.6;
  color: ${({ $palette }) => $palette.muted};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 1.25rem;

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 1.2rem;
  border-radius: 20px;
  background: ${({ $palette }) => $palette.card};
  border: 1px solid ${({ $palette }) => $palette.border};
`;

const InfoList = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ $palette }) => $palette.text};
  opacity: 0.92;
`;

const IconWrap = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: ${({ $palette }) => $palette.iconBg};
  color: ${({ $palette }) => $palette.iconFg};
`;

const Form = styled.form`
  display: grid;
  gap: 0.9rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid ${({ $palette }) => $palette.border};
  background: ${({ $palette }) => $palette.input};
  color: ${({ $palette }) => $palette.text};
  border-radius: 16px;
  padding: 0.95rem 1rem;
  outline: none;

  &::placeholder {
    color: ${({ $palette }) => $palette.muted};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 160px;
  border: 1px solid ${({ $palette }) => $palette.border};
  background: ${({ $palette }) => $palette.input};
  color: ${({ $palette }) => $palette.text};
  border-radius: 16px;
  padding: 0.95rem 1rem;
  outline: none;
  resize: vertical;

  &::placeholder {
    color: ${({ $palette }) => $palette.muted};
  }
`;

const Submit = styled.button`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.9rem 1.25rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  background: ${({ $palette }) => $palette.buttonBg};
  color: ${({ $palette }) => $palette.buttonText};
  font-weight: 700;
  transition: transform 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.75;
    cursor: wait;
  }
`;

const Feedback = styled.p`
  margin: 0;
  color: ${({ $type }) => ($type === 'success' ? '#2ec27e' : '#ff6b6b')};
`;

const defaultPalette = {
  surface: 'rgba(16, 18, 24, 0.78)',
  card: 'rgba(255, 255, 255, 0.04)',
  input: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.12)',
  text: '#f8fafc',
  muted: 'rgba(248, 250, 252, 0.72)',
  iconBg: 'rgba(255, 255, 255, 0.08)',
  iconFg: '#f8fafc',
  buttonBg: '#f8fafc',
  buttonText: '#111827',
  shadow: '0 30px 80px rgba(0, 0, 0, 0.24)',
};

export default function PublicContactSection({ profile, palette = {}, eyebrow = 'Contact', title = 'Send a message', subtitle = 'Reach out directly from this portfolio.' }) {
  const mergedPalette = { ...defaultPalette, ...palette };
  const { mutate: sendMessage, isPending } = useSendMessage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [feedback, setFeedback] = useState(null);

  if (!profile?.id) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFeedback(null);

    sendMessage(
      {
        profile: profile.id,
        name: formData.name,
        email: formData.email,
        message: formData.message,
      },
      {
        onSuccess: () => {
          setFormData({ name: '', email: '', message: '' });
          setFeedback({ type: 'success', text: 'Your message was sent.' });
        },
        onError: () => {
          setFeedback({ type: 'error', text: 'Message delivery failed. Please try again.' });
        },
      }
    );
  };

  return (
    <Section $palette={mergedPalette}>
      <Header>
        <Eyebrow $palette={mergedPalette}>{eyebrow}</Eyebrow>
        <Title>{title}</Title>
        <Subtitle $palette={mergedPalette}>{subtitle}</Subtitle>
      </Header>

      <Grid>
        <Card $palette={mergedPalette}>
          <InfoList>
            {profile?.email && (
              <InfoRow $palette={mergedPalette}>
                <IconWrap $palette={mergedPalette}><Mail size={16} /></IconWrap>
                <span>{profile.email}</span>
              </InfoRow>
            )}
            {profile?.phone && (
              <InfoRow $palette={mergedPalette}>
                <IconWrap $palette={mergedPalette}><Phone size={16} /></IconWrap>
                <a href={`tel:${profile.phone}`} style={{ color: 'inherit' }}>{profile.phone}</a>
              </InfoRow>
            )}
            {(profile?.country || profile?.city) && (
              <InfoRow $palette={mergedPalette}>
                <IconWrap $palette={mergedPalette}><MapPin size={16} /></IconWrap>
                <span>{[profile.city, profile.country].filter(Boolean).join(', ')}</span>
              </InfoRow>
            )}
          </InfoList>
        </Card>

        <Card $palette={mergedPalette}>
          <Form onSubmit={handleSubmit}>
            <FieldRow>
              <Input $palette={mergedPalette} name="name" placeholder="Your name" value={formData.name} onChange={handleChange} required />
              <Input $palette={mergedPalette} name="email" type="email" placeholder="Your email" value={formData.email} onChange={handleChange} required />
            </FieldRow>
            <Textarea $palette={mergedPalette} name="message" placeholder="What would you like to talk about?" value={formData.message} onChange={handleChange} required />
            {feedback && <Feedback $type={feedback.type}>{feedback.text}</Feedback>}
            <Submit $palette={mergedPalette} type="submit" disabled={isPending}>
              <Send size={16} />
              {isPending ? 'Sending...' : 'Send message'}
            </Submit>
          </Form>
        </Card>
      </Grid>
    </Section>
  );
}
